import React from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { HtmlIframe } from './HtmlIframe';

interface Props {
  content: string;
  /**
   * Surface-specific renderer overrides (e.g. inline-styled ul/table for the
   * admin panel, Tailwind prose for the user/partner chats). Custom `code` /
   * `pre` are honoured for non-html blocks; the html-iframe path always wins
   * so every surface gets the same chart rendering with no drift.
   */
  extraComponents?: Partial<Components>;
}

/**
 * The single source of truth for rendering an assistant message body.
 *
 * Always parses Markdown + GFM and intercepts ```html ``` fenced blocks,
 * rendering them via `HtmlIframe` so each surface (admin AI assistant,
 * partner/user chat, public share page) shows charts identically without
 * duplicating the code/pre renderer logic.
 */
export const MessageMarkdown: React.FC<Props> = ({ content, extraComponents }) => {
  // react-markdown's Components type is `keyof JSX.IntrinsicElements | FC`; cast
  // to a callable so we can delegate to surface-specific overrides cleanly.
  const extraCode = extraComponents?.code as ((p: any) => React.ReactNode) | undefined;
  const extraPre = extraComponents?.pre as ((p: any) => React.ReactNode) | undefined;

  // Strip code/pre from the surface overrides — we wrap them below so the html
  // iframe path always wins, and the surface's non-html code/pre handler is
  // delegated to via extraCode / extraPre instead.
  const { code: _c, pre: _p, ...restExtras } = extraComponents || {};

  const components = {
    ...restExtras,
    code: (props: any) => {
      const lang = (props.className as string | undefined)?.replace('language-', '') || '';
      if (lang === 'html') {
        const text = String(props.children).replace(/\n$/, '');
        return <HtmlIframe content={text} />;
      }
      if (extraCode) return extraCode(props);
      return (
        <code className={props.className} {...props}>
          {props.children}
        </code>
      );
    },
    pre: (props: any) => {
      const codeChild = props.node?.children?.[0];
      const isHtml = codeChild?.properties?.className?.some?.(
        (c: string) => c === 'language-html',
      );
      if (isHtml) {
        return <>{props.children}</>;
      }
      if (extraPre) return extraPre(props);
      return <pre {...props} />;
    },
  } as unknown as Components;

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {content}
    </ReactMarkdown>
  );
};
